import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { TenantAuthClaims } from '@models';
import { Observable, catchError, first, from, of, switchMap } from 'rxjs';
import { PlanAlertService } from '../services/plan-alert.service';
import { TenantContextService } from '@services';
import { classifyPlanAccess } from '@utils/lib/plan-access';

/**
 * Guard that blocks access when the tenant's plan is expired or suspended.
 * Super-admins always bypass this check.
 * Redirects to /plan-expired with ?status=<planStatus> for context-aware UI.
 */
export function planActiveGuard(): CanActivateFn {
  return (): Observable<boolean | UrlTree> => {
    const auth = inject(Auth);
    const router = inject(Router);
    const db = inject(Firestore);
    const planAlert = inject(PlanAlertService);
    const tenantContext = inject(TenantContextService);

    return from(auth.authStateReady()).pipe(
      switchMap(() => authState(auth).pipe(first())),
      catchError(() => of(null)),
      switchMap((user) => {
        if (!user) return of(router.createUrlTree(['/login']));

        return from(user.getIdTokenResult()).pipe(
          switchMap((tokenResult) => {
            const claims = tokenResult.claims as unknown as TenantAuthClaims;

            // Super-admins bypass plan check
            if (claims?.superAdmin === true) return of(true);

            // Resolve the *active* tenant from the switcher state, not always [0].
            let tenantId: string | null = null;
            try {
              tenantId = tenantContext.tenantId;
            } catch {
              tenantId = claims?.tenants
                ? (Object.keys(claims.tenants)[0] ?? null)
                : ((claims as any)?.tenantId ?? null);
            }
            if (!tenantId) return of(router.createUrlTree(['/login']));

            // Read the tenant doc to check plan status
            const tenantRef = doc(db, 'tenants', tenantId);
            return from(getDoc(tenantRef)).pipe(
              switchMap((snap) => {
                if (!snap.exists()) return of(router.createUrlTree(['/login']));

                const tenant = snap.data() as any;

                // Plan decision lives in `classifyPlanAccess` (single source
                // of truth shared with the embed widget functions).
                const access = classifyPlanAccess(tenant);

                // Hard block: user cannot access the dashboard at all
                if (access.access === 'blocked') {
                  planAlert.clear();
                  return of(
                    router.createUrlTree(['/plan-expired'], {
                      queryParams: { status: access.status },
                    }),
                  );
                }

                // Degraded: user can access but sees a payment banner
                if (access.access === 'degraded') {
                  planAlert.set(access.status);
                  return of(true);
                }

                planAlert.clear();
                return of(true);
              }),
              catchError(() => of(true)), // on error, allow through to avoid blocking
            );
          }),
          catchError(() => of(router.createUrlTree(['/login']))),
        );
      }),
    );
  };
}
